import { createSelector } from '@reduxjs/toolkit';
import {
  getAllBusinessUnits,
  getBusinessUnitById,
} from './BusinessUnit.selector';
import { getStructure } from './Structure.selector';
import { NormalizedGroup } from '../GroupsAdapter';
import { getGroupsChildIdsFromGroupSelector } from './Group.selector';
import { toBase64 } from '../../../shared/util/base64';

export const getAllStores = createSelector([getStructure], (structure) => {
  return structure.data?.stores.map(
    (store) => structure.data?.entities.groups[store] as NormalizedGroup
  );
});

export const getAllStoreChildIdsFromGroupSelector = createSelector(
  [getStructure, getGroupsChildIdsFromGroupSelector],
  (structure, groupChildIds) => {
    return groupChildIds?.filter((encodedId) =>
      structure.data?.stores.includes(encodedId)
    );
  }
);

export const getStoresFromBusinessUnit = createSelector(
  [getStructure, getBusinessUnitById],
  (structure, businessUnit) => {
    return {
      ...businessUnit,
      childGroups:
        businessUnit?.childGroups?.map(
          (id) => structure.data?.entities.groups[id]
        ) ?? [],
    };
  }
);

export const getBusinessUnitFromStoreSelector = createSelector(
  [
    getStructure,
    getAllBusinessUnits,
    (_state, props: { storeId: string; workspaceId: string | undefined }) =>
      props.storeId,
  ],
  (structure, businessUnits, storeId) => {
    const groupDictionary = structure.data?.entities?.groups;
    if (!groupDictionary) return undefined;
    if (!storeId) return undefined;

    const businessUnitIds = new Set(
      (businessUnits ?? []).map((businessUnit) => businessUnit.id)
    );

    const encodedStoreId = toBase64(storeId);

    // Walk up the ancestry tree until we hit an existing business unit id.
    let currentGroup =
      groupDictionary[storeId] ??
      groupDictionary[encodedStoreId] ??
      Object.values(groupDictionary).find((group) => group.uuid === storeId);

    if (!currentGroup) return undefined;
    if (businessUnitIds.has(currentGroup.id)) return currentGroup;

    const visited = new Set<string>();

    while (currentGroup?.parentGroupId) {
      const parentGroupId = currentGroup.parentGroupId;
      if (visited.has(parentGroupId)) break;
      visited.add(parentGroupId);

      const parentGroup = groupDictionary[parentGroupId];
      if (!parentGroup) return undefined;

      currentGroup = parentGroup;
      if (businessUnitIds.has(currentGroup.id)) {
        return currentGroup;
      }
    }

    return undefined;
  }
);
